import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Pagination,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Chip,
  AppBar,
  Toolbar,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
} from "@mui/material";
import { URL as API_URL } from "../constants/url";
import colors from "../constants/colors";
import { Download, People, FileDownload } from "@mui/icons-material";

const AllUsers = ({ adminData, searchText }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const ITEMS_PER_PAGE = itemsPerPage;

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/get-all-users`);
      const data = await res.json();
      if (data && data.success) {
        setUsers(data.data || []);
      } else {
        setUsers([]);
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filtered = useMemo(() => {
    if (!searchText || searchText.trim() === "") return users;
    const q = searchText.toLowerCase();
    return users.filter((u) => {
      return (
        (u.name || "").toLowerCase().includes(q) ||
        (u.phone || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        ((u.address || u.shortAddress || (u.addresses && u.addresses[0]?.address) || "") + "").toLowerCase().includes(q)
      );
    });
  }, [users, searchText]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));

  const paginated = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filtered.slice(start, start + ITEMS_PER_PAGE);
  }, [filtered, page, ITEMS_PER_PAGE]);

  useEffect(() => {
    setPage(1);
  }, [searchText, itemsPerPage]);

  const getAddress = (u) => {
    return (
      u.address || u.shortAddress || (u.addresses && u.addresses[0]?.address) || "" 
    );
  };

  const escapeCSV = (val) => {
    if (val === undefined || val === null) return "";
    const s = String(val);
    return `"${s.replace(/"/g, '""')}"`;
  };

  const exportCSV = (filename = "all-users.csv") => {
    const headers = ["Name", "Phone Number", "Email", "Address"];
    const rows = users.map((u) => [u.name || "", u.phone || "", u.email || "", getAddress(u)]);
    let csv = headers.join(",") + "\n";
    csv += rows.map((r) => r.map(escapeCSV).join(",")).join("\n");

    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const exportExcel = (filename = "all-users.xls") => {
    const headers = ["Name", "Phone Number", "Email", "Address"];
    const rows = users.map((u) => [u.name || "", u.phone || "", u.email || "", getAddress(u)]);
    let csv = headers.join("\t") + "\n";
    csv += rows.map((r) => r.map((c) => (c || "")).join("\t")).join("\n");

    const blob = new Blob([csv], { type: "application/vnd.ms-excel;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: colors.darkWhite,
      }}
    >
      {/* AppBar with Export Buttons */}
      <AppBar
        position="sticky"
        sx={{
          bgcolor: "white",
          color: colors.darkBlack,
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            gap: 2,
            alignItems: "center",
            minHeight: "64px !important",
            px: { xs: 2, sm: 3 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Tabs
              value={0}
              sx={{
                minHeight: "48px",
                "& .MuiTabs-indicator": {
                  backgroundColor: colors.green,
                  height: 3,
                  borderRadius: 3,
                },
                "& .MuiTab-root": {
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
                  minWidth: "auto",
                  px: { xs: 1, sm: 3 },
                  minHeight: "48px",
                  whiteSpace: "nowrap",
                },
                "& .MuiTab-root.Mui-selected": {
                  color: colors.green,
                },
              }}
            >
              <Tab
                icon={<People sx={{ mr: 1, fontSize: { xs: "18px", sm: "20px" } }} />}
                iconPosition="start"
                label={isMobile ? "Users" : "All Users"}
              />
            </Tabs>
            
            <Chip
              label={`${users.length} Users`}
              sx={{
                backgroundColor: colors.lightGreen,
                color: colors.darkGreen,
                fontWeight: 600,
              }}
            />
          </Box>

          {/* Export Buttons - Right side of AppBar */}
          <Stack direction="row" spacing={1}>
            {isMobile ? (
              <>
                <Tooltip title="Export CSV">
                  <IconButton
                    onClick={() => exportCSV("all-users.csv")}
                    sx={{
                      color: colors.green,
                      border: `1px solid ${colors.green}`,
                    }}
                  >
                    <FileDownload />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Export Excel">
                  <IconButton
                    onClick={() => exportExcel("all-users.xls")}
                    sx={{
                      backgroundColor: colors.green,
                      color: "white",
                      "&:hover": {
                        backgroundColor: colors.darkGreen,
                      },
                    }}
                  >
                    <Download />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <>
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={() => exportCSV("all-users.csv")}
                  sx={{
                    borderColor: colors.green,
                    color: colors.green,
                    fontWeight: 600,
                  }}
                >
                  Export CSV
                </Button>

                <Button
                  variant="contained"
                  startIcon={<Download />}
                  onClick={() => exportExcel("all-users.xls")}
                  sx={{
                    backgroundColor: colors.green,
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: colors.darkGreen,
                    },
                  }}
                >
                  Export Excel
                </Button>
              </>
            )}
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Main Content - Table with Pagination */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
          p: { xs: 2, sm: 3 },
        }}
      >
        <Paper
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            border: `1px solid ${colors.lightGray}`,
            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 10,
                flexGrow: 1,
              }}
            >
              <CircularProgress size={60} sx={{ color: colors.green }} />
            </Box>
          ) : filtered.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                py: 10,
                flexGrow: 1,
                textAlign: "center",
              }}
            >
              <People sx={{ fontSize: 64, color: colors.midGray, mb: 2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                No users found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchText ? "Try a different search term" : "No users in the system"}
              </Typography>
            </Box>
          ) : (
            <>
              {/* Table */}
              <TableContainer sx={{ flexGrow: 1, overflow: "auto" }}>
                <Table sx={{ minWidth: 650 }} size={isMobile ? "small" : "medium"}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: colors.lightGreen }}>
                      <TableCell sx={{ fontWeight: 700, color: colors.darkGreen, py: 2 }}>
                        Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: colors.darkGreen, py: 2 }}>
                        Phone Number
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: colors.darkGreen, py: 2 }}>
                        Email
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: colors.darkGreen, py: 2 }}>
                        Address
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginated.map((u) => (
                      <TableRow 
                        key={u._id} 
                        sx={{ 
                          '&:last-child td, &:last-child th': { border: 0 },
                          '&:hover': {
                            backgroundColor: `${colors.lightGreen}20`,
                          },
                          transition: 'background-color 0.2s',
                        }}
                      >
                        <TableCell sx={{ py: 2, fontWeight: 500 }}>
                          {u.name || "-"}
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          {u.phone || "-"}
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          {u.email || "-"}
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            py: 2,
                            maxWidth: { xs: "150px", sm: "250px", md: "350px", lg: "450px" },
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {getAddress(u) || "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <Box 
                sx={{ 
                  display: "flex", 
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "space-between", 
                  alignItems: "center",
                  p: { xs: 2, sm: 3 },
                  borderTop: `1px solid ${colors.lightGray}`,
                  gap: { xs: 2, sm: 0 },
                }}
              >
                <Typography variant="body2" sx={{ color: colors.darkGray, fontWeight: 500 }}>
                  Showing {(page - 1) * ITEMS_PER_PAGE + 1} - {Math.min(page * ITEMS_PER_PAGE, filtered.length)} of {filtered.length} users
                </Typography>

                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(e, value) => setPage(value)}
                  color="primary"
                  siblingCount={isMobile ? 0 : 1}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      fontWeight: 600,
                    },
                    '& .MuiPaginationItem-page.Mui-selected': {
                      backgroundColor: colors.green,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: colors.darkGreen,
                      },
                    },
                  }}
                />
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default AllUsers;